import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isEqual } from 'lodash-es';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useCurrentUser, userSelectors } from '@marvelapp/ballpark-application';
import { BtwButton, BtwInput, BtwLink, BtwSettingsItem, Stack, } from '@marvelapp/ballpark-components';
import { useUpdatePassword } from '../../hooks/useUpdatePassword';
const INPUT_STATES = {
    ready: {
        variant: 'primary',
        text: 'Set password',
        placeholder: 'Enter a strong password',
        isDisabled: false,
    },
    passwordSet: {
        variant: 'secondary',
        text: 'Reset password',
        placeholder: '••••••••••••••••',
        isDisabled: true,
    },
};
export const PasswordProtectionSetting = observer(function PasswordProtectionSetting({ isPasswordProtected, prototypePk, userTestPk, }) {
    const [passwordValue, setPasswordValue] = useState('');
    const [updatePasswordFn] = useUpdatePassword(prototypePk);
    const [isInvalid, setIsInvalid] = useState(false);
    const [isPasswordSettingToggled, setIsPasswordSettingToggled] = useState(!!isPasswordProtected);
    const [inputState, setInputState] = useState(isPasswordProtected ? INPUT_STATES.passwordSet : INPUT_STATES.ready);
    const updatePassword = (password) => {
        updatePasswordFn({ variables: { itemId: prototypePk, password } });
    };
    const setPassword = (password) => {
        if (!password)
            return;
        updatePassword(password);
        setInputState(INPUT_STATES.passwordSet);
        if (isInvalid)
            setIsInvalid(false);
    };
    const clearPassword = () => {
        updatePassword('');
        setPasswordValue('');
        setInputState(INPUT_STATES.ready);
    };
    return (_jsxs(Stack, { align: "stretch", "data-testid": "password-protection-setting", gap: "3", width: "full", className: "px-5 py-4", children: [_jsx(PasswordProtectToggle, { clearPassword: clearPassword, isInvalid: isInvalid, isPasswordSettingToggled: isPasswordSettingToggled, setIsInvalid: setIsInvalid, setIsPasswordSettingToggled: setIsPasswordSettingToggled, userTestPk: userTestPk }), isPasswordSettingToggled && (_jsx("form", { autoComplete: "new-password", "data-testid": "password-form", onSubmit: (e) => {
                    e.preventDefault();
                    if (isEqual(inputState, INPUT_STATES.passwordSet)) {
                        clearPassword();
                    }
                    else {
                        setPassword(passwordValue);
                    }
                }, children: _jsxs(Stack, { gap: "2", width: "full", children: [_jsxs(Stack, { direction: "row", align: "end", gap: "2", width: "full", children: [_jsx(BtwInput.Root, { className: "flex-1", size: "intermediate", children: _jsx(BtwInput.Field, { "data-testid": "password-8-chars", disabled: inputState.isDisabled, id: "builder-password-input", minLength: 8, onChange: (e) => {
                                            setPasswordValue(e.target.value);
                                            if (isInvalid)
                                                setIsInvalid(false);
                                        }, onInvalid: (e) => {
                                            e.preventDefault();
                                            setIsInvalid(true);
                                        }, placeholder: inputState.placeholder, type: "password", value: passwordValue }) }), _jsx(BtwButton, { type: "submit", "data-testid": "set-password-button", variant: inputState.variant, size: "intermediate", children: inputState.text })] }), isInvalid && (_jsx(BtwInput.Error, { children: "Enter 8 characters or more" }))] }) }))] }));
});
const PasswordProtectToggle = observer(function PasswordProtectToggle({ clearPassword, isInvalid, isPasswordSettingToggled, setIsInvalid, setIsPasswordSettingToggled, userTestPk, }) {
    const user = useCurrentUser();
    const isLockedFreePlan = userSelectors.isLockedFreePlan(user);
    const togglePasswordSetting = () => {
        if (isLockedFreePlan) {
            return;
        }
        if (isInvalid) {
            setIsInvalid(false);
        }
        if (isPasswordSettingToggled) {
            clearPassword();
            setIsPasswordSettingToggled(false);
        }
        else {
            setIsPasswordSettingToggled(true);
        }
    };
    const isChecked = isLockedFreePlan ? false : isPasswordSettingToggled;
    return (_jsxs(_Fragment, { children: [_jsx(BtwSettingsItem, { "data-testid": "password-protect-toggle", description: "Anyone viewing the link will be asked to enter a password", disabled: isLockedFreePlan, heading: "Password protect", id: "password-protect", onPressedChange: togglePasswordSetting, pressed: isChecked, variant: "primary", size: "sm" }), isLockedFreePlan && (_jsx("div", { className: "mt-1", children: _jsx(BtwLink, { asChild: true, underline: true, size: "sm", children: _jsx(RouterLink, { to: `/plans?origin=/project/${userTestPk}`, children: "Upgrade to enable" }) }) }))] }));
});
